import { useState } from 'react';
import { Download, Copy, Check, Package, MapPin, Bell, User, Settings, LogOut, Star, TrendingUp } from 'lucide-react';

interface DesignSystemScreenProps {
  onNavigate: (screen: string) => void;
}

export function DesignSystemScreen({ onNavigate }: DesignSystemScreenProps) {
  const [activeTab, setActiveTab] = useState<'colors' | 'typography' | 'components' | 'icons' | 'prototype'>('prototype');
  const [copied, setCopied] = useState(false);

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const designTokens = {
    colors: {
      primary: {
        main: '#E0001B',
        light: '#FF1A35',
        dark: '#C00017',
        50: '#FFE5E8',
        900: '#800010'
      },
      accent: {
        main: '#00B277',
        light: '#00D68F',
        dark: '#008F5F'
      },
      neutral: {
        white: '#FFFFFF',
        50: '#F9FAFB',
        100: '#F3F4F6',
        200: '#E5E7EB',
        600: '#4B5563',
        900: '#111827',
        black: '#000000'
      },
      semantic: {
        success: '#10B981',
        warning: '#F59E0B',
        error: '#EF4444',
        info: '#3B82F6'
      }
    },
    spacing: {
      xs: '4px',
      sm: '8px',
      md: '16px',
      lg: '24px',
      xl: '32px'
    },
    borderRadius: {
      sm: '8px',
      md: '12px',
      lg: '16px',
      xl: '24px',
      full: '9999px'
    },
    typography: {
      h1: { size: '28px', weight: 700, lineHeight: '1.25' },
      h2: { size: '22px', weight: 600, lineHeight: '1.25' },
      h3: { size: '18px', weight: 600, lineHeight: '1.25' },
      body: { size: '16px', weight: 400, lineHeight: '1.5' },
      small: { size: '14px', weight: 400, lineHeight: '1.5' },
      caption: { size: '12px', weight: 500, lineHeight: '1.5' }
    }
  };

  const exportJSON = () => {
    const json = JSON.stringify(designTokens, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'go7-design-tokens.json';
    a.click();
  };

  const prototypeFlows = [
    { name: 'Splash Screen', screen: 'splash', color: 'bg-purple-100 text-purple-700' },
    { name: 'Onboarding', screen: 'onboarding', color: 'bg-blue-100 text-blue-700' },
    { name: 'Authentication', section: true },
    { name: 'Login / OTP', screen: 'login', color: 'bg-green-100 text-green-700' },
    { name: '✨ Sign Up (New)', screen: 'signup', color: 'bg-blue-100 text-blue-700' },
    { name: '📋 Application Review', screen: 'application-review', color: 'bg-yellow-100 text-yellow-700' },
    { name: '🎓 Training & Certification', screen: 'training', color: 'bg-purple-100 text-purple-700' },
    { name: 'Main Flow', section: true },
    { name: 'Permissions', screen: 'permissions', color: 'bg-yellow-100 text-yellow-700' },
    { name: 'Home Jobs Feed', screen: 'home', color: 'bg-red-100 text-red-700' },
    { name: 'Job Detail', screen: 'job-detail', color: 'bg-pink-100 text-pink-700' },
    { name: 'Navigation', screen: 'navigation', color: 'bg-indigo-100 text-indigo-700' },
    { name: 'Pickup Confirmation', screen: 'pickup-confirmation', color: 'bg-teal-100 text-teal-700' },
    { name: 'Delivery Confirmation', screen: 'delivery-confirmation', color: 'bg-orange-100 text-orange-700' },
    { name: 'Features', section: true },
    { name: 'Earnings / Wallet', screen: 'earnings', color: 'bg-emerald-100 text-emerald-700' },
    { name: 'Shift Management', screen: 'shift-management', color: 'bg-cyan-100 text-cyan-700' },
    { name: '📊 Performance Dashboard', screen: 'performance', color: 'bg-purple-100 text-purple-700' },
    { name: '🎁 Bonuses & Quests', screen: 'promotions', color: 'bg-yellow-100 text-yellow-700' },
    { name: '🏆 Leaderboard', screen: 'leaderboard', color: 'bg-orange-100 text-orange-700' },
    { name: 'History', screen: 'history', color: 'bg-amber-100 text-amber-700' },
    { name: 'Profile & Settings', section: true },
    { name: 'Profile', screen: 'profile', color: 'bg-violet-100 text-violet-700' },
    { name: '✏️ Edit Profile', screen: 'edit-profile', color: 'bg-blue-100 text-blue-700' },
    { name: 'Settings', screen: 'settings', color: 'bg-fuchsia-100 text-fuchsia-700' },
    { name: '⚙️ Advanced Settings', screen: 'advanced-settings', color: 'bg-blue-100 text-blue-700' },
    { name: '🚗 Vehicle Management', screen: 'vehicle-management', color: 'bg-green-100 text-green-700' },
    { name: 'Support', section: true },
    { name: '💬 Help Center', screen: 'help-center', color: 'bg-teal-100 text-teal-700' },
    { name: 'Incident Report', screen: 'incident-report', color: 'bg-rose-100 text-rose-700' },
    { name: 'Communication', section: true },
    { name: '🔔 Notifications', screen: 'notifications', color: 'bg-blue-100 text-blue-700' },
    { name: '💬 Customer Chat', screen: 'chat', color: 'bg-green-100 text-green-700' },
    { name: 'Safety & Compliance', section: true },
    { name: '🛡️ Gear Compliance Check', screen: 'gear-compliance', color: 'bg-red-100 text-red-700' },
    { name: '🆘 SOS/Panic Button', screen: 'sos-demo', color: 'bg-red-100 text-red-700' },
  ];

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#E0001B] to-[#FF1A35] text-white px-4 py-6">
        <h1 className="text-[24px] mb-1">GO7 Design System</h1>
        <p className="text-[14px] text-white/90">Rider App v2.4.1</p>
      </div>

      {/* Tabs */}
      <div className="bg-white border-b border-gray-200 overflow-x-auto">
        <div className="flex px-4">
          {(['prototype', 'colors', 'typography', 'components', 'icons'] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 h-12 text-[14px] capitalize border-b-2 transition-colors whitespace-nowrap ${
                activeTab === tab
                  ? 'border-[#E0001B] text-[#E0001B]'
                  : 'border-transparent text-[#6B6B6B] hover:text-[#111111]'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4">
        {/* Prototype Tab */}
        {activeTab === 'prototype' && (
          <div className="space-y-4">
            <div className="bg-white rounded-[16px] p-4">
              <h2 className="text-[18px] text-[#111111] mb-2">Clickable Prototype</h2>
              <p className="text-[14px] text-[#6B6B6B] mb-4">
                Navigate through all screens to test the complete rider journey
              </p>
              
              <div className="grid grid-cols-1 gap-2">
                {prototypeFlows.map((flow, index) => {
                  if ((flow as any).section) {
                    return (
                      <div key={flow.name} className="mt-4 mb-2">
                        <h3 className="text-[14px] font-bold text-[#111111] uppercase tracking-wide">
                          {flow.name}
                        </h3>
                      </div>
                    );
                  }
                  
                  return (
                    <button
                      key={flow.screen}
                      onClick={() => onNavigate(flow.screen!)}
                      className={`h-14 rounded-[12px] ${flow.color} flex items-center justify-between px-4 hover:opacity-80 transition-opacity`}
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-[14px]">{flow.name}</span>
                      </div>
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Job Flow Diagram */}
            <div className="bg-white rounded-[16px] p-4">
              <h2 className="text-[18px] text-[#111111] mb-4">Complete Job Flow</h2>
              <div className="space-y-2">
                {[
                  'Login with OTP',
                  'View Available Jobs',
                  'Select & Accept Job',
                  'Navigate to Pickup',
                  'Confirm Pickup (Photo)',
                  'Navigate to Delivery',
                  'Complete Delivery (Photo + Signature + Cash)',
                  'View Earnings'
                ].map((step, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-[#E0001B] text-white flex items-center justify-center text-[12px] flex-shrink-0 mt-1">
                      {index + 1}
                    </div>
                    <p className="text-[14px] text-[#111111] pt-1">{step}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Colors Tab */}
        {activeTab === 'colors' && (
          <div className="space-y-4">
            <div className="bg-white rounded-[16px] p-4">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-[18px] text-[#111111]">Color Tokens</h2>
                <button
                  onClick={exportJSON}
                  className="h-10 px-4 rounded-[12px] bg-[#E0001B] text-white flex items-center gap-2 text-[14px]"
                >
                  <Download className="w-4 h-4" />
                  Export JSON
                </button>
              </div>

              {/* Primary Colors */}
              <div className="mb-6">
                <h3 className="text-[14px] text-[#6B6B6B] mb-3">Primary - Brand Red</h3>
                <div className="grid grid-cols-2 gap-2">
                  {Object.entries(designTokens.colors.primary).map(([key, value]) => (
                    <button
                      key={key}
                      onClick={() => copyToClipboard(value)}
                      className="h-20 rounded-[12px] flex flex-col items-center justify-center gap-1 hover:scale-105 transition-transform"
                      style={{ backgroundColor: value }}
                    >
                      <span className={`text-[12px] ${['main', 'dark', '900'].includes(key) ? 'text-white' : 'text-[#111111]'}`}>
                        {key}
                      </span>
                      <span className={`text-[10px] ${['main', 'dark', '900'].includes(key) ? 'text-white/80' : 'text-[#6B6B6B]'}`}>
                        {value}
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Accent Colors */}
              <div className="mb-6">
                <h3 className="text-[14px] text-[#6B6B6B] mb-3">Accent - Success Green</h3>
                <div className="grid grid-cols-3 gap-2">
                  {Object.entries(designTokens.colors.accent).map(([key, value]) => (
                    <button
                      key={key}
                      onClick={() => copyToClipboard(value)}
                      className="h-20 rounded-[12px] flex flex-col items-center justify-center gap-1 hover:scale-105 transition-transform text-white"
                      style={{ backgroundColor: value }}
                    >
                      <span className="text-[12px]">{key}</span>
                      <span className="text-[10px] text-white/80">{value}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Semantic Colors */}
              <div>
                <h3 className="text-[14px] text-[#6B6B6B] mb-3">Semantic Colors</h3>
                <div className="grid grid-cols-2 gap-2">
                  {Object.entries(designTokens.colors.semantic).map(([key, value]) => (
                    <button
                      key={key}
                      onClick={() => copyToClipboard(value)}
                      className="h-16 rounded-[12px] flex flex-col items-center justify-center gap-1 hover:scale-105 transition-transform text-white capitalize"
                      style={{ backgroundColor: value }}
                    >
                      <span className="text-[12px]">{key}</span>
                      <span className="text-[10px] text-white/80">{value}</span>
                    </button>
                  ))}
                </div>
              </div>

              {copied && (
                <div className="fixed bottom-4 left-1/2 -translate-x-1/2 bg-[#111111] text-white px-4 py-2 rounded-[12px] flex items-center gap-2 animate-fade-in">
                  <Check className="w-4 h-4" />
                  <span className="text-[14px]">Color copied!</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Typography Tab */}
        {activeTab === 'typography' && (
          <div className="space-y-4">
            <div className="bg-white rounded-[16px] p-4">
              <h2 className="text-[18px] text-[#111111] mb-4">Typography Scale</h2>
              
              <div className="space-y-6">
                {Object.entries(designTokens.typography).map(([name, props]) => (
                  <div key={name} className="border-b border-gray-100 pb-4 last:border-0">
                    <div className="flex items-baseline justify-between mb-2">
                      <span className="text-[12px] text-[#6B6B6B] uppercase">{name}</span>
                      <span className="text-[10px] text-[#6B6B6B] font-mono">
                        {props.size} / {props.weight}
                      </span>
                    </div>
                    <p
                      style={{
                        fontSize: props.size,
                        fontWeight: props.weight,
                        lineHeight: props.lineHeight
                      }}
                    >
                      The quick brown fox jumps
                    </p>
                  </div>
                ))}
              </div>

              <div className="mt-6 p-4 bg-gray-50 rounded-[12px]">
                <p className="text-[12px] text-[#6B6B6B] mb-2">Font Family</p>
                <p className="text-[14px] text-[#111111] font-mono">
                  Inter (400, 500, 600, 700)
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Components Tab */}
        {activeTab === 'components' && (
          <div className="space-y-4">
            {/* Buttons */}
            <div className="bg-white rounded-[16px] p-4">
              <h2 className="text-[18px] text-[#111111] mb-4">Buttons</h2>
              <div className="space-y-3">
                <button className="w-full h-12 rounded-[12px] bg-[#E0001B] text-white hover:bg-[#C00017] transition-colors">
                  Primary Button
                </button>
                <button className="w-full h-12 rounded-[12px] border-2 border-[#E0001B] text-[#E0001B] hover:bg-[#E0001B]/5 transition-colors">
                  Secondary Button
                </button>
                <button className="w-full h-12 rounded-[12px] bg-gray-100 text-[#111111] hover:bg-gray-200 transition-colors">
                  Tertiary Button
                </button>
                <button className="w-full h-12 rounded-[12px] bg-[#E0001B] text-white opacity-50 cursor-not-allowed">
                  Disabled Button
                </button>
              </div>
            </div>

            {/* Cards */}
            <div className="bg-white rounded-[16px] p-4">
              <h2 className="text-[18px] text-[#111111] mb-4">Cards</h2>
              <div className="space-y-3">
                <div className="bg-gray-50 rounded-[12px] p-4 border border-gray-200">
                  <h3 className="text-[16px] text-[#111111] mb-1">Card Title</h3>
                  <p className="text-[14px] text-[#6B6B6B]">Card description with supporting text</p>
                </div>
                <div className="bg-gradient-to-r from-[#E0001B] to-[#FF1A35] rounded-[16px] p-4 text-white">
                  <h3 className="text-[16px] mb-1">Gradient Card</h3>
                  <p className="text-[14px] text-white/90">For important CTAs and highlights</p>
                </div>
              </div>
            </div>

            {/* Inputs */}
            <div className="bg-white rounded-[16px] p-4">
              <h2 className="text-[18px] text-[#111111] mb-4">Form Elements</h2>
              <div className="space-y-3">
                <input
                  type="text"
                  placeholder="Text Input"
                  className="w-full h-12 px-4 rounded-[12px] border-2 border-gray-200 focus:border-[#E0001B] focus:outline-none transition-colors"
                />
                <textarea
                  placeholder="Textarea"
                  rows={4}
                  className="w-full px-4 py-3 rounded-[12px] border-2 border-gray-200 focus:border-[#E0001B] focus:outline-none transition-colors resize-none"
                />
              </div>
            </div>

            {/* Badges */}
            <div className="bg-white rounded-[16px] p-4">
              <h2 className="text-[18px] text-[#111111] mb-4">Badges & Tags</h2>
              <div className="flex flex-wrap gap-2">
                <span className="px-3 py-1 rounded-full bg-[#E0001B] text-white text-[12px]">Active</span>
                <span className="px-3 py-1 rounded-full bg-[#00B277] text-white text-[12px]">Completed</span>
                <span className="px-3 py-1 rounded-full bg-amber-500 text-white text-[12px]">Pending</span>
                <span className="px-3 py-1 rounded-full bg-gray-200 text-[#111111] text-[12px]">Inactive</span>
              </div>
            </div>
          </div>
        )}

        {/* Icons Tab */}
        {activeTab === 'icons' && (
          <div className="space-y-4">
            <div className="bg-white rounded-[16px] p-4">
              <h2 className="text-[18px] text-[#111111] mb-4">Icon Library</h2>
              <p className="text-[14px] text-[#6B6B6B] mb-4">
                Using Lucide React icons at 20px, 24px, and 32px sizes
              </p>
              
              <div className="grid grid-cols-4 gap-3">
                {[
                  { Icon: Package, name: 'Package' },
                  { Icon: MapPin, name: 'Location' },
                  { Icon: Bell, name: 'Notifications' },
                  { Icon: User, name: 'Profile' },
                  { Icon: Settings, name: 'Settings' },
                  { Icon: LogOut, name: 'Logout' },
                  { Icon: Star, name: 'Rating' },
                  { Icon: TrendingUp, name: 'Growth' },
                ].map(({ Icon, name }) => (
                  <div key={name} className="flex flex-col items-center gap-2 p-3 rounded-[12px] bg-gray-50 hover:bg-gray-100 transition-colors">
                    <Icon className="w-6 h-6 text-[#E0001B]" />
                    <span className="text-[10px] text-[#6B6B6B] text-center">{name}</span>
                  </div>
                ))}
              </div>

              <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-[12px]">
                <p className="text-[12px] text-blue-900 mb-2">Export Guidelines</p>
                <ul className="text-[12px] text-blue-700 space-y-1">
                  <li>• SVG format (optimized)</li>
                  <li>• Sizes: 24px, 32px, 48px</li>
                  <li>• Stroke width: 2px</li>
                  <li>• Use currentColor for fill</li>
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* Design Specs */}
        <div className="bg-white rounded-[16px] p-4">
          <h2 className="text-[18px] text-[#111111] mb-4">Design Specifications</h2>
          
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-[12px] text-[#6B6B6B] mb-1">Screen Width</p>
                <p className="text-[16px] text-[#111111]">360px</p>
              </div>
              <div>
                <p className="text-[12px] text-[#6B6B6B] mb-1">Grid System</p>
                <p className="text-[16px] text-[#111111]">8px base</p>
              </div>
              <div>
                <p className="text-[12px] text-[#6B6B6B] mb-1">Touch Target</p>
                <p className="text-[16px] text-[#111111]">48px min</p>
              </div>
              <div>
                <p className="text-[12px] text-[#6B6B6B] mb-1">Border Radius</p>
                <p className="text-[16px] text-[#111111]">12-16px</p>
              </div>
            </div>
          </div>
        </div>

        {/* Export Assets */}
        <div className="bg-gradient-to-r from-[#E0001B] to-[#FF1A35] rounded-[16px] p-4 text-white">
          <h2 className="text-[18px] mb-2">Export Assets</h2>
          <p className="text-[14px] text-white/90 mb-4">
            Download complete design system with tokens, components, and documentation
          </p>
          <button
            onClick={exportJSON}
            className="w-full h-12 rounded-[12px] bg-white text-[#E0001B] hover:bg-white/90 transition-colors flex items-center justify-center gap-2"
          >
            <Download className="w-5 h-5" />
            Download Design System
          </button>
        </div>
      </div>
    </div>
  );
}